/* eslint-disable max-len */
import React from "react";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useFormik } from "formik";
import { Auth } from "@aws-amplify/auth";

import * as Cognito from "../providers/cognito";
import * as Axios from "providers/axios";
import { Button, BUTTON_SIZES } from "../components/Button";
import AuthInput from "components/AuthInput";
import arrowRightIcon from "assets/arrow_right.svg";
import AuthLayout from "components/AuthLayout";

/* eslint-disable no-console */

export default function Authenticate() {
  const navigate = useNavigate();
  const { api } = Axios.useAxios();
  const { fetchAuthenticatedUser } = Cognito.useCognito();
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");

  const { handleSubmit, handleChange, touched, errors, values } = useFormik({
    initialValues: {
      email: "",
      password: ""
    },
    validationSchema: AuthenticateSchema,
    validateOnChange: true,
    validateOnMount: false,
    validateOnBlur: false,
    onSubmit: async ({
      email,
      password
    }: {
      email: string;
      password: string;
    }) => {
      const emailLowerCase = encodeURIComponent(email.toLowerCase());

      try {
        setSubmitting(true);

        const user = await Auth.signIn(email.toLowerCase(), password);

        if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
          return navigate(`/set-new-password?email=${emailLowerCase}`);
        }

        if (user.attributes.sub) {
          fetchAuthenticatedUser();

          await api.post("/v1/users/login", {
            email: user.attributes.email,
            cognito_id: user.attributes.sub
          });
        }
      } catch (error: any) {
        if (error.code === "UserNotConfirmedException") {
          return navigate(`/verify?email=${emailLowerCase}`);
        }
        setError(error.message);
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <>
      <Helmet>
        <title>Pointment | Login</title>
        <meta property="og:title" content="Pointment | Login" />
      </Helmet>
      <AuthLayout
        title="Log in"
        error={error}
        setError={setError}
        cyId="sign-in"
      >
        <>
          {/*<div>
            <Button
              className="w-full rounded-lg !text-[15px] !font-['Inter'] !font-medium !border-[rgba(17, 23, 32, 0.9)]"
              text="Continue with Google"
              type="submit"
              variant={BUTTON_VARIANTS.GOOGLE}
              size={BUTTON_SIZES.MEDIUM}
              onClick={signInGoogle}
            />
            <Button
              className="mt-4 w-full rounded-lg !text-[15px] !font-['Inter'] !font-medium"
              text="Continue with Apple"
              type="submit"
              variant={BUTTON_VARIANTS.APPLE}
              size={BUTTON_SIZES.MEDIUM}
              onClick={signInApple}
            />
          </div>
          <div className="flex w-full items-center justify-between my-4">
            <div className="border-b border-[#CCC7C0] w-2/5" />
            <p className="forms text-xs text-[#B0ACA6] font-['Inter']">OR</p>
            <div className="border-b border-[#CCC7C0] w-2/5" />
          </div>*/}
          <div>
            <form onSubmit={handleSubmit}>
              <AuthInput
                type="email"
                name="email"
                label="Email"
                cyId="email-input"
                placeholder="hello@pointmentapp.com"
                value={values.email}
                onChange={handleChange}
                touched={touched}
                errors={errors}
                autocapitalize="none"
              />
              <AuthInput
                type="password"
                name="password"
                label="Password"
                cyId="password-input"
                placeholder="Password"
                value={values.password}
                onChange={handleChange}
                touched={touched}
                errors={errors}
              />
              <div className="flex gap-[16px]">
                <Link
                  className="cursor-pointer w-full"
                  to={"/register"}
                  tabIndex={-1}
                >
                  <Button
                    className="w-full rounded-lg !bg-mono-tertiary !border !border-mono-primary !text-[15px] !font-['Inter'] !font-medium !text-mono-primary 
                      hover:!text-mono-primary hover:!bg-mono-secondary focus:!bg-mono-secondary whitespace-nowrap !px-0"
                    text={"New to Pointment?"}
                    size={BUTTON_SIZES.MEDIUM}
                    type="button"
                  />
                </Link>
                <Button
                  className="w-auto rounded-lg !bg-mono-primary border-none !text-[15px] !font-['Inter'] !font-medium text-mono-white 
                    hover:!text-mono-white hover:!bg-mono-primaryLight focus:!bg-mono-primaryLight whitespace-nowrap !px-7 disabled:!bg-mono-300"
                  text={"Log in"}
                  type="submit"
                  size={BUTTON_SIZES.MEDIUM}
                  cyId="submit-sign-in-button"
                  submitting={submitting}
                  disabledOnSubmit
                />
              </div>
              <div className="h-px w-full mt-8 mb-4 bg-mono-100" />
              <Link className="cursor-pointer" to={"/forgot-password"}>
                <div className="inline-flex">
                  <p className="text-[14px] font-normal font-['Inter']">
                    Forgot your password?
                  </p>
                  <img
                    className="ml-1"
                    src={arrowRightIcon}
                    alt="arrow right"
                  />
                </div>
              </Link>
            </form>
          </div>
        </>
      </AuthLayout>
    </>
  );
}

const AuthenticateSchema = Yup.object().shape({
  email: Yup.string().email().required("This field is required"),
  password: Yup.string().required("This field is required")
});
